/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  IMediaModel,
  MediaType,
  RouteHelper,
  useCanWatchAuthContent,
  useIsPlayableWithDate,
} from "@nf/common";
import { EXTERNAL_LINKS } from "@nf/constants";
import { useMemo } from "react";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";

import Button, { ButtonSize } from "components/Button";
import { useSyncCompleted } from "hooks";

import PlayIcon from "resources/icons/play.svg";

const OfferButton = dynamic(() => import("components/OfferButton"));

export interface PlayButtonProps {
  media?: IMediaModel;
  size?: ButtonSize;
}

export const PlayButton = ({ media, size }: PlayButtonProps) => {
  const { t } = useTranslation("translations");
  const { canWatch } = useCanWatchAuthContent(media);
  const { isComingSoon, isAvailable, availableFrom } =
    useIsPlayableWithDate(media);
  const isAuthProcessCompleted = useSyncCompleted();
  const isCollection = media?.MediaTypeCode === MediaType.Collection;

  const variant = isComingSoon ? "secondary" : "primary";

  const state = useMemo(() => {
    if (!isAuthProcessCompleted) {
      return "skeleton";
    }

    if (isComingSoon) {
      return "disabled";
    }

    return "default";
  }, [isAuthProcessCompleted, isComingSoon]);

  const href = useMemo(() => {
    if (!media || !isAuthProcessCompleted) {
      return undefined;
    }

    if (!isAvailable) {
      return EXTERNAL_LINKS.WISH_LIST;
    }

    if (isCollection) {
      return RouteHelper.goToDetails(media);
    }

    return RouteHelper.goToPlayer(media);
  }, [media, isAuthProcessCompleted, isAvailable, isCollection]);

  const label = useMemo(() => {
    if (isComingSoon) {
      return t("web_asset_page_avaliable_from", { date: availableFrom });
    }

    if (!isAvailable) {
      return t("web_asset_page_not_available_link");
    }

    return t("web_highlight_asset_button_watch_now");
  }, [isComingSoon, availableFrom, isAvailable]);

  const icon = isComingSoon || !isAvailable ? null : <PlayIcon />;

  if (!media || (!canWatch && isAuthProcessCompleted)) {
    return <OfferButton size={size} />;
  }

  return (
    <Button href={href} variant={variant} size={size} icon={icon} state={state}>
      {label}
    </Button>
  );
};

export default PlayButton;
